import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import ArticleCardList from '@/components/blog/ArticleCardList';
import ArticleTagList from '@/components/blog/ArticleTagList';
import CustomHeading from '@/components/blog/CustomHeading';
import JsonLd from '@/components/common/JsonLd';
import LazyVideoPlayer from '@/components/common/LazyVideoPlayer';
import ProductRecoCarousel from '@/components/layout/carouselV3/ProductRecoCarousel';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { NS } from '@/constants';
import { ARTICLE_BLOC_TYPE, ARTICLE_CARD_TYPE, MINIMUM_IMG_SIZE, POSITION_TYPE } from '@/constants/blog';
import { PAGE_TYPE } from '@/constants/datalayer';
import { URLS } from '@/constants/urls';
import buildLinkedData from '@/linkedData/article-lkd';
import { useAppSelector } from '@/redux';
import { useServices } from '@/services/useServices';
import { getBlogImageSrc } from '@/utils/blogUtils';
import { carouselProductsToSlidesProps } from '@/utils/millesimaUtils';
import styles from './ArticleBlogPage.module.scss';

export const ArticleBlogPage = ({ crossReading, articleAggregation }) => {
	const { t } = useTranslation(NS.BLOG);
	const storeConf = useAppSelector(({ root }) => root.storeConf);
	const { locale } = storeConf;
	const json = useMemo(() => buildLinkedData(articleAggregation?.articles[0], storeConf), [articleAggregation, storeConf]);
	const isMobile = useScreenSizeContext().isMobile;
	const articleImageSrc = getBlogImageSrc(articleAggregation.articles[0].imageName, isMobile, ARTICLE_CARD_TYPE.LARGE);
	const blocs = articleAggregation.articles[0].blocs;
	const customization = {
		customClassName: { articleCardList: styles['cross-reading'] },
		headingLevel: { articleCardList: 2, articleCard: 3 },
		position: { articleCard: POSITION_TYPE.START },
	};
	const { datalayerService } = useServices();

	useEffect(() => {
		datalayerService.pushPageName(PAGE_TYPE.BLOG_ARTICLE, PAGE_TYPE.BLOG_ARTICLE);
	}, []);

	const getBlocComponent = (bloc, index) => {
		const { type } = bloc;

		let element: JSX.Element;

		switch (type) {
			case ARTICLE_BLOC_TYPE.TEXT_BLOC:
				element = <div className={styles['text-bloc-container']} dangerouslySetInnerHTML={{ __html: bloc.content }} />;
				break;
			case ARTICLE_BLOC_TYPE.HTML_BLOC:
				element = <div className={styles['html-bloc-container']} dangerouslySetInnerHTML={{ __html: bloc.content }} />;
				break;
			case ARTICLE_BLOC_TYPE.IMAGE_BLOC:
				element = (
					<div className={styles['image-bloc-container']}>
						<img
							loading="lazy"
							className={styles['image-bloc']}
							src={getBlogImageSrc(bloc.image, isMobile, ARTICLE_CARD_TYPE.LARGE)}
							alt={bloc.imageAlt}
						/>
					</div>
				);
				break;
			case ARTICLE_BLOC_TYPE.VIDEO_BLOC:
				element = (
					<LazyVideoPlayer
						video={{ idYoutube: bloc.content }}
						img={bloc.image || URLS.BG_VIDEO_IMG}
						customClassName={{ video: styles['video'] }}
					/>
				);
				break;
			case ARTICLE_BLOC_TYPE.PRODUCT_CAROUSEL:
				const slides = carouselProductsToSlidesProps({ products: bloc.carouselResult?.products, withDetails: false });
				element = slides?.length > 0 && <ProductRecoCarousel withGap slides={slides} position="blog" />;
				break;
			default:
		}

		return (
			element && (
				<div key={index} className={styles['bloc']}>
					{element}
				</div>
			)
		);
	};

	const fullName =
		!articleAggregation.articles[0].author.firstName || !articleAggregation.articles[0].author.lastName
			? t('articleCard.missingAuthor')
			: `${articleAggregation.articles[0].author.firstName} ${articleAggregation.articles[0].author.lastName}`;

	const Author = <p className={styles['card-text']}>{`${t('articleCard.byAuthor')} ${fullName}`}</p>;
	const PublicationDate = <p>{new Date(articleAggregation.articles[0].publishedAt).toLocaleDateString(locale)}</p>;
	const ShortDesc = <p className={styles['short-desc']}>{articleAggregation.articles[0].shortDesc}</p>;

	return (
		<>
			{articleImageSrc && (
				<Head>
					<link rel="preload" href={articleImageSrc} as="image" />
				</Head>
			)}
			{json && <JsonLd data={json} />}
			<div className={styles.container} data-rbf="blog-article-page">
				<div className={styles['article-top']}>
					<ArticleTagList tags={articleAggregation.tags} centered={true} articlePage={true} />
					<CustomHeading
						title={articleAggregation.articles[0].title}
						headingLevel={1}
						labelPosition={POSITION_TYPE.CENTER}
						customClassName={{ label: styles['title'] }}
					/>
					<div className={styles['author-date-detail']}>
						{Author}
						{PublicationDate}
					</div>
					<img
						className={styles['img-top']}
						src={articleImageSrc}
						alt={articleAggregation.articles[0].imageAlt}
						width={MINIMUM_IMG_SIZE.WIDTH}
						height={MINIMUM_IMG_SIZE.HEIGHT}
					/>
				</div>

				{ShortDesc}
				{blocs?.map((bloc, index) => getBlocComponent(bloc, index))}

				<ArticleCardList
					title={t('articlePage.alsoRead')}
					articles={crossReading}
					articleCardType={ARTICLE_CARD_TYPE.SMALL}
					customization={customization}
				/>
			</div>
		</>
	);
};

ArticleBlogPage.propTypes = {
	crossReading: PropTypes.array.isRequired,
	articleAggregation: PropTypes.shape({
		articleAggregationIdentifier: PropTypes.string.isRequired,
		articles: PropTypes.array.isRequired,
		tags: PropTypes.array.isRequired,
		id: PropTypes.string,
	}).isRequired,
};

export default ArticleBlogPage;
